import {CommonModule, Location, PlatformLocation} from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {AdminUser} from '@gatewee/types/admin-user';
import {DomainUser, RecipientUser} from '@gatewee/types/user';
import {TranslateModule} from '@ngx-translate/core';
import {TuiDropdown, TuiHint} from '@taiga-ui/core';
import {GateweeExpandChevronComponent} from '../../gatewee-expand-chevron/gatewee-expand-chevron.component';
import {GateweeIconComponent} from '../../gatewee-icon/gatewee-icon.component';
import {
  NavigationMenuItem,
  NavigationMenuStateService,
} from '../../services/navigation-menu-state.service';
import {GateweeNavFlyoutComponent} from './gatewee-nav-flyout/gatewee-nav-flyout.component';

const tuiDropdown = [...TuiDropdown] as const;

export type MenuSubItem = {
  title: string;
  route?: string;
  theme?: string;
  subBadge?: boolean;
  openInNewTab?: boolean;
  subsubMenu?: {title: string; route: string}[];
};

export interface MenuItem {
  title: string;
  icon?: string;
  route?: string;
  badge?: boolean;
  /** When true, `route` opens in a new browser tab instead of in-app navigation. */
  openInNewTab?: boolean;
  subMenu?: MenuSubItem[];
}

export interface NavigationConfig {
  menuItems?: MenuItem[];
  profile?: RecipientUser | DomainUser | AdminUser;
  onMenuClick?: (item: MenuItem) => void;
}

@Component({
  selector: 'gatewee-navigation',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TranslateModule,
    GateweeIconComponent,
    GateweeExpandChevronComponent,
    TuiHint,
    ...tuiDropdown,
    GateweeNavFlyoutComponent,
  ],
  templateUrl: './gatewee-navigation.component.html',
  styleUrls: ['./gatewee-navigation.component.css'],
})
export class GateweeNavigationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() config: NavigationConfig | null = null;
  @Input() isCollapsed = false;
  @Output() closeSideMenuMobile = new EventEmitter<boolean>();
  @Output() navigationToggle = new EventEmitter<boolean>();

  activeToggle = '';
  localData = '';
  elementValue = '';
  currentHref = '';
  sidebarStyle = '';
  activeMenu = '';
  activeSubMenu = '';
  activeSubSubMenu = '';

  /** Top-level items that have `subMenu`: manual expand when URL is not in section. */
  readonly expandedTopWithSub = new Set<string>();

  /** Subitems that have `subsubMenu`: manual expand when URL is not under that subitem. */
  readonly expandedSubMenuWithNested = new Set<string>();

  /** Tracks open state of collapsed-rail flyout dropdowns, keyed by item title. */
  collapsedOpenState: Record<string, boolean> = {};

  profile!: RecipientUser | undefined;
  sidebarMenu: MenuItem[] = [];

  public isVisited = false;

  private themePollInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private backLocation: PlatformLocation,
    private navigationMenuStateService: NavigationMenuStateService,
    private ngZone: NgZone,
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const path = this.location.path();
        this.handleActiveMenu(path);
      }
    });
    backLocation.onPopState(() => {
      const path = this.location.path();
      this.handleActiveMenu(path);
    });
  }

  ngAfterViewInit(): void {
    this.profile = JSON.parse(localStorage.getItem('profile') || '{}');
  }

  ngOnDestroy(): void {
    if (this.themePollInterval !== null) {
      clearInterval(this.themePollInterval);
      this.themePollInterval = null;
    }
  }

  hoverAdd(): void {
    const getStyle = document.body.getAttribute('data-sidebar-style');
    if (getStyle === 'icon-hover') {
      // return val;
    }
  }

  checkVisited(): void {
    this.isVisited = !this.isVisited;
  }

  isActive(title: string, level: 'menu' | 'submenu' | 'subsubmenu'): boolean {
    switch (level) {
      case 'menu':
        return this.activeMenu === title;
      case 'submenu':
        return this.activeSubMenu === title;
      case 'subsubmenu':
        return this.activeSubSubMenu === title;
      default:
        return false;
    }
  }

  /** Whether a top-level item with `subMenu` is expanded (URL in section or manual). */
  isTopMenuOpen(item: MenuItem): boolean {
    if (!item.subMenu?.length) {
      return false;
    }
    return (
      this.isActive(item.title, 'menu') ||
      this.expandedTopWithSub.has(item.title)
    );
  }

  /** True when this top-level item has its own non-empty `route` (not only children). */
  parentHasOwnRoute(item: MenuItem): boolean {
    const r = item.route;
    return typeof r === 'string' && r.trim().length > 0;
  }

  /** Closes the collapsed flyout for `title` and optionally closes mobile nav. */
  closeCollapsedDropdown(title: string): void {
    this.collapsedOpenState[title] = false;
    this.closeMobileNavIfNarrow();
  }

  /**
   * Handles a click on the left `<a>` when the parent has its own `route`
   * (navigate + close mobile drawer when narrow).
   */
  onTopMenuLinkClick(): void {
    // Parent items with children should not close mobile nav.
  }

  /**
   * Parent has `subMenu` but no own route: left control is a button — toggle
   * accordion like the chevron (no navigation, no link to first child).
   */
  onTopMenuParentNoRouteClick(item: MenuItem): void {
    this.toggleTopMenuExpandedState(item);
  }

  closeMobileNavForTopLeaf(item: MenuItem): void {
    if (item.subMenu?.length) {
      return;
    }
    this.closeMobileNavIfNarrow();
  }

  closeMobileNavForSubLeaf(subitem: MenuSubItem): void {
    if (subitem.subsubMenu?.length) {
      return;
    }
    this.closeMobileNavIfNarrow();
  }

  toggleTopMenuExpand(item: MenuItem, event: Event): void {
    event.stopPropagation();
    this.toggleTopMenuExpandedState(item);
  }

  private toggleTopMenuExpandedState(item: MenuItem): void {
    if (this.expandedTopWithSub.has(item.title)) {
      this.expandedTopWithSub.delete(item.title);
    } else {
      // Desktop accordion behavior: keep only one top-level branch open.
      if (window.innerWidth > 940) {
        this.expandedTopWithSub.clear();
      }
      this.expandedTopWithSub.add(item.title);
    }
    if (this.config?.onMenuClick && item) {
      this.config.onMenuClick(item);
    }
  }

  subMenuExpandKey(parentTitle: string, subTitle: string): string {
    return `${parentTitle}::${subTitle}`;
  }

  /** Sub-submenu panel visible when URL implies this branch or user expanded manually. */
  isSubBranchOpen(parent: MenuItem, subitem: MenuSubItem): boolean {
    if (!subitem.subsubMenu?.length) {
      return true;
    }
    return (
      this.isActive(subitem.title, 'submenu') ||
      this.expandedSubMenuWithNested.has(
        this.subMenuExpandKey(parent.title, subitem.title),
      )
    );
  }

  toggleSubMenuExpand(
    parent: MenuItem,
    subitem: MenuSubItem,
    event: Event,
  ): void {
    event.stopPropagation();
    const key = this.subMenuExpandKey(parent.title, subitem.title);
    if (this.expandedSubMenuWithNested.has(key)) {
      this.expandedSubMenuWithNested.delete(key);
    } else {
      this.expandedSubMenuWithNested.add(key);
    }
  }

  closeMobileNavIfNarrow(): void {
    if (window.innerWidth <= 940) {
      this.closeSideMenuMobile.emit(false);
    }
  }

  externalHref(route: string): string {
    return new URL(route, window.location.origin).href;
  }

  ngOnInit(): void {
    this.elementValue = document.body.getAttribute('data-theme-version') ?? '';
    // Run the theme poll OUTSIDE Angular zone so the 300ms tick does not
    // trigger a full app change-detection cycle every time. Only when the
    // value actually changes do we re-enter the zone to update the binding.
    this.ngZone.runOutsideAngular(() => {
      this.themePollInterval = setInterval(() => {
        const next = document.body.getAttribute('data-theme-version') ?? '';
        if (next !== this.elementValue) {
          this.ngZone.run(() => {
            this.elementValue = next;
          });
        }
      }, 300);
    });

    this.profile = JSON.parse(localStorage.getItem('profile') || '{}');

    // Initialize menu items
    if (this.config?.menuItems) {
      this.sidebarMenu = [...this.config.menuItems];
    } else {
      this.sidebarMenu = this.getDefaultMenu();
    }

    this.navigationMenuStateService.setMenuItems(
      this.sidebarMenu as NavigationMenuItem[],
    );

    const path = this.router.url || this.location.path();
    this.handleActiveMenu(path);
  }

  private getDefaultMenu(): MenuItem[] {
    return [
      {
        title: 'nav.menu.dashboard',
        icon: '@tui.line-chart',
        route: '/panel',
      },
      {
        title: 'nav.menu.clients',
        icon: '@tui.user',
        route: '/panel/clients',
      },
      {
        title: 'nav.menu.invoices',
        icon: '@tui.file-text',
        route: '/panel/invoices',
      },
      {
        title: 'nav.menu.checkouts',
        icon: '@tui.tag',
        route: '/panel/checkouts',
      },
      {
        title: 'nav.menu.transactions',
        icon: '@tui.arrow-left-right',
        route: '/panel/transactions',
      },
      {
        title: 'nav.menu.subscriptions',
        icon: '@tui.clipboard-list',
        badge: true,
        subMenu: [
          {
            title: 'nav.menu.subscriptions',
            route: '/panel/subscriptions',
          },
          {
            title: 'nav.menu.subscriptionsPlans',
            route: '/panel/subscriptions-plans',
          },
        ],
      },
      {
        title: 'nav.menu.withdrawals',
        icon: '@tui.arrow-down-circle',
        route: '/panel/withdrawals',
      },
      {
        title: 'nav.menu.chargebacks',
        icon: '@tui.alert-circle',
        route: '/panel/chargebacks',
      },
      {
        title: 'nav.menu.settings',
        icon: '@tui.settings',
        subMenu: [
          {
            title: 'nav.menu.whatsapp',
            route: '/panel/settings/whatsapp',
          },
          {
            title: 'nav.menu.apiKeys',
            route: '/panel/recipients/keys',
          },
          {
            title: 'nav.menu.users',
            route: '/panel/settings/users',
          },
        ],
      },
    ];
  }

  themeMode(menu: string): void {
    this.activeSubMenu = '';
    localStorage.setItem('data-theme-version', menu);
    this.localData = localStorage.getItem('data-theme-version') ?? '';
    this.elementValue = this.localData;
    document.body.setAttribute('data-theme-version', this.localData);
  }

  /**
   * Syncs active section from the current path. Optionally handles `openInNewTab` from click.
   * In-app navigation is handled by `routerLink`; do not call this for internal routes except from `NavigationEnd`.
   */
  handleActiveMenu(val: string, subItem?: MenuItem | MenuSubItem): void {
    const cleanVal = val.split('?')[0].split('#')[0].split(';')[0];

    if (subItem?.route && subItem.openInNewTab) {
      const url = new URL(subItem.route, window.location.origin).href;
      window.open(url, '_blank', 'noopener,noreferrer');
      if (window.innerWidth <= 940) {
        this.closeSideMenuMobile.emit(false);
      }
      return;
    }

    this.activeMenu = '';
    this.activeSubMenu = '';
    this.activeSubSubMenu = '';

    this.sidebarMenu.forEach((data: MenuItem) => {
      if (data.route && cleanVal.startsWith(data.route)) {
        this.activeMenu = data.title;
      }

      data.subMenu?.forEach(item => {
        let matchedSubsub = false;
        item.subsubMenu?.forEach(item1 => {
          if (cleanVal.startsWith(item1.route)) {
            this.activeMenu = data.title;
            this.activeSubMenu = item.title;
            this.activeSubSubMenu = item1.title;
            matchedSubsub = true;
          }
        });
        if (!matchedSubsub && item.route && cleanVal.startsWith(item.route)) {
          this.activeMenu = data.title;
          this.activeSubMenu = item.title;
          this.activeSubSubMenu = '';
        }
      });
    });

    if (window.innerWidth <= 940 && subItem?.route) {
      this.closeSideMenuMobile.emit(false);
    }
  }

  handleMenu(val: string, item?: MenuItem): void {
    if (this.activeMenu === 'Icons') {
      this.activeMenu = val;
    } else {
      if (this.activeMenu === val) {
        this.activeMenu = '';
      } else {
        this.activeMenu = val;
      }
    }

    if (window.innerWidth <= 940 && item?.route) {
      this.closeSideMenuMobile.emit(false);
    }

    if (this.config?.onMenuClick && item) {
      this.config.onMenuClick(item);
    }
  }

  toggleNavigation(): void {
    this.navigationToggle.emit(!this.isCollapsed);
  }
}
